import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import { TreePalm } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/shadcn/card'
import { Skeleton } from '@/components/shadcn/skeleton'
import { Separator } from '@/components/shadcn/separator'
import { useGetApiCompaniesCurrentServicemodulesAbsenceServiceModuleIdVacationquota } from '@/api/generated/company-user/company-user'

interface Props {
  serviceModuleId: number
  employeeId: string
}

export function EmployeeVacationCard({ serviceModuleId, employeeId }: Props) {
  const { t } = useTranslation('employees')
  const currentYear = dayjs().year()

  const { data, isLoading } =
    useGetApiCompaniesCurrentServicemodulesAbsenceServiceModuleIdVacationquota(
      serviceModuleId,
      { employeeId, loadSurplus: true },
      { query: { enabled: !!serviceModuleId && !!employeeId } }
    )

  const quotas = useMemo(() => {
    const items = Array.isArray(data?.data) ? data.data : []
    return items.filter(
      (q) => dayjs(q.validFrom).year() === currentYear || dayjs(q.validUntil).year() === currentYear
    )
  }, [data, currentYear])

  const header = (
    <>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <TreePalm className="size-4" />
          {t('detail.quotas.vacation', { year: currentYear })}
        </CardTitle>
      </CardHeader>
      <Separator />
    </>
  )

  if (isLoading) {
    return (
      <Card>
        {header}
        <CardContent className="pt-4">
          <div className="flex flex-col gap-2">
            <Skeleton className="h-4 w-48" />
            <Skeleton className="h-4 w-32" />
          </div>
        </CardContent>
      </Card>
    )
  }

  // Nichts anzuzeigen → Karte komplett ausblenden.
  if (quotas.length === 0) return null

  return (
    <Card>
      {header}
      <CardContent className="pt-4">
        <div className="flex flex-col gap-1.5">
          {quotas.map((quota) => (
            <div key={quota.id}>
              <p className="text-sm">
                {dayjs(quota.validFrom).format('DD.MM.YYYY')}
                {' – '}
                {quota.validUntil ? dayjs(quota.validUntil).format('DD.MM.YYYY') : t('detail.quotas.open')}
                : <span className="font-medium">{t('detail.quotas.vacationDays', { days: quota.totalVacationDays ?? 0 })}</span>
              </p>
              {(quota.surplusFromPrevious ?? 0) > 0 && (
                <p className="text-xs text-muted-foreground">
                  {t('detail.quotas.surplus', { days: quota.surplusFromPrevious })}
                  {quota.surplusValidUntil &&
                    t('detail.quotas.surplusValidUntil', { date: dayjs(quota.surplusValidUntil).format('DD.MM.YYYY') })}
                </p>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}
